import {
  ChatJessWebSocketEvent,
  BackendAgentMessage,
} from "../../pages/Dashboard/ChatJess/types";
import { logger } from "../../utils/logger";
import { getLoggedinUser } from "../../api/apiCore";
import { transformBackendMessage } from "./transformBackendMessage";
import { URLS } from "../../constants";

// Singleton pattern: only one WebSocket connection at a time
let currentSocket: WebSocket | null = null;
let currentHeartbeatInterval: NodeJS.Timeout | null = null;
const heartbeatInterval = 30000; // 30 seconds

let currentOpenHandler: ((event: Event) => void) | null = null;
let currentMessageHandler: ((event: MessageEvent) => void) | null = null;
let currentCloseHandler: ((event: CloseEvent) => void) | null = null;
let currentErrorHandler: ((event: Event) => void) | null = null;

/**
 * Disconnects the current ChatJess WebSocket connection if it exists
 */
export const disconnectChatJessWebSocket = (): void => {
  if (currentSocket) {
    // Clear heartbeat interval
    if (currentHeartbeatInterval) {
      clearInterval(currentHeartbeatInterval);
      currentHeartbeatInterval = null;
    }

    if (currentOpenHandler) {
      currentSocket.removeEventListener("open", currentOpenHandler);
      currentOpenHandler = null;
    }
    if (currentMessageHandler) {
      currentSocket.removeEventListener("message", currentMessageHandler);
      currentMessageHandler = null;
    }
    if (currentCloseHandler) {
      currentSocket.removeEventListener("close", currentCloseHandler);
      currentCloseHandler = null;
    }
    if (currentErrorHandler) {
      currentSocket.removeEventListener("error", currentErrorHandler);
      currentErrorHandler = null;
    }

    currentSocket.onopen = null;
    currentSocket.onmessage = null;
    currentSocket.onclose = null;
    currentSocket.onerror = null;

    // Close the connection if it's still open
    if (
      currentSocket.readyState === WebSocket.OPEN ||
      currentSocket.readyState === WebSocket.CONNECTING
    ) {
      currentSocket.close(1000, "Replacing with new connection");
    }

    currentSocket = null;
  }
};

/**
 * Connects to the real ChatJess WebSocket server
 * Only one connection can exist at a time - previous connections are automatically closed
 */
export const connectToRealChatJessWebSocket = (
  onMessage: (event: ChatJessWebSocketEvent) => void,
  onConnectionChange?: (connected: boolean) => void,
  onError?: (error: Error) => void,
): WebSocket => {
  // Get user data from localStorage
  const user = getLoggedinUser();

  if (!user) {
    const error = new Error("Usuário não autenticado");
    onError?.(error);
    throw error;
  }

  const companyId = user.companyID || user.companyId;
  const telephone = user.telephone;

  if (!companyId) {
    const error = new Error("ID da empresa não encontrado");
    logger.error("[ChatJess WS Real] Erro:", error);
    onError?.(error);
    throw error;
  }

  if (!telephone) {
    const error = new Error("Telefone do usuário não encontrado");
    logger.error("[ChatJess WS Real] Erro:", error);
    onError?.(error);
    throw error;
  }

  // Build clientId from telephone
  const clientId = `${telephone}@c.us`;

  // Use direct agent-proxy WebSocket URL when available (avoids dev server proxy
  // which doesn't support WebSocket upgrades). Falls back to PROCESS_API_BASE_URL.
  let wsBaseUrl: string;
  if (URLS.USE_AGENT_PROXY === "true" && URLS.AGENT_PROXY_WS_URL) {
    wsBaseUrl = URLS.AGENT_PROXY_WS_URL;
  } else {
    const baseUrl =
      process.env.REACT_APP_PROCESS_API_BASE_URL ||
      "https://dev-agent-proxy.jesss.ai";
    wsBaseUrl = baseUrl.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
  }

  // Build WebSocket URL with authentication token
  const params = new URLSearchParams();
  if (companyId) {
    params.append("x-company-id", companyId);
  }

  const wsUrl = `${wsBaseUrl}/api/agent/websocket/messages/${companyId}/${clientId}?${params.toString()}`;

  currentSocket = new WebSocket(wsUrl);

  currentOpenHandler = () => {
    onConnectionChange?.(true);

    // Enviar heartbeat inicial
    if (currentSocket?.readyState === WebSocket.OPEN) {
      currentSocket.send(
        JSON.stringify({
          type: "heartbeat",
          timestamp: new Date().toISOString(),
        }),
      );
    }
  };
  currentSocket.addEventListener("open", currentOpenHandler);

  currentMessageHandler = (event) => {
    try {
      const rawData = JSON.parse(event.data);

      // Check if this is a backend AgentMessage with requestType
      if (rawData.requestType) {
        // This is a backend message, transform it
        const backendMessage = rawData as BackendAgentMessage;
        const transformedEvent = transformBackendMessage(backendMessage);

        if (transformedEvent) {
          onMessage(transformedEvent);
        }
      } else {
        // This might be an old format message, pass it through
        const data: ChatJessWebSocketEvent = rawData;
        onMessage(data);
      }
    } catch (error) {
      onError?.(error as Error);
    }
  };
  currentSocket.addEventListener("message", currentMessageHandler);

  currentCloseHandler = () => {
    // Clear heartbeat interval
    if (currentHeartbeatInterval) {
      clearInterval(currentHeartbeatInterval);
      currentHeartbeatInterval = null;
    }

    onConnectionChange?.(false);

    // Clear the reference only if this is the current socket
    if (currentSocket?.readyState === WebSocket.CLOSED) {
      currentSocket = null;
    }
  };
  currentSocket.addEventListener("close", currentCloseHandler);

  currentErrorHandler = (event) => {
    logger.error("[ChatJess WS Real] Erro na conexão:", event);
    const error = new Error("Erro na conexão WebSocket");
    onError?.(error);
    onConnectionChange?.(false);
  };
  currentSocket.addEventListener("error", currentErrorHandler);

  currentHeartbeatInterval = setInterval(() => {
    if (currentSocket?.readyState === WebSocket.OPEN) {
      currentSocket.send(
        JSON.stringify({
          type: "heartbeat",
          timestamp: new Date().toISOString(),
        }),
      );
    } else {
      if (currentHeartbeatInterval) {
        clearInterval(currentHeartbeatInterval);
        currentHeartbeatInterval = null;
      }
    }
  }, heartbeatInterval);

  return currentSocket;
};

/**
 * Get the current ChatJess WebSocket connection if it exists and is open
 */
export const getCurrentChatJessWebSocket = (): WebSocket | null => {
  if (currentSocket?.readyState === WebSocket.OPEN) {
    return currentSocket;
  }
  return null;
};

/**
 * Check if there's an active ChatJess WebSocket connection
 */
export const isChatJessWebSocketConnected = (): boolean => {
  return currentSocket?.readyState === WebSocket.OPEN;
};
